/**
 * The fields of a credit-request decision -- grant or decline, an amount, a
 * note. Pure and controlled: `DecisionDialog` owns the state and the
 * mutation, so this file stays free of both and is exercised directly by
 * its own tests with no network and no dialog around it.
 */

import { useId, type ReactNode } from 'react';

import { defineCatalogue } from '../../i18n';
import { Field, FieldDescription, FieldLabel } from '@/ui/field';
import { Input } from '@/ui/input';
import { Label } from '@/ui/label';
import { RadioGroup, RadioGroupItem } from '@/ui/radio-group';
import { Textarea } from '@/ui/textarea';

const catalogue = defineCatalogue(
  {
    'decision.grant': 'Conceder',
    'decision.decline': 'Recusar',
    'decision.credits.label': 'Créditos a conceder',
    'decision.credits.hint': 'Adicionado ao saldo da organização assim que você conceder.',
    'decision.slots.label': 'Vagas a conceder',
    'decision.slots.hint':
      'Adicionado às vagas de campanha da organização assim que você conceder.',
    'decision.note.label': 'Nota',
    'decision.note.hint': 'Mostrada para a organização junto com a sua decisão.',
  },
  {
    'decision.grant': 'Grant',
    'decision.decline': 'Decline',
    'decision.credits.label': 'Credits to grant',
    'decision.credits.hint': "Added to the organisation's balance once you grant it.",
    'decision.slots.label': 'Slots to grant',
    'decision.slots.hint': "Added to the organisation's campaign slots once you grant it.",
    'decision.note.label': 'Note',
    'decision.note.hint': 'Shown to the organisation together with your decision.',
  },
);
const t = catalogue.t;

export type Decision = 'GRANTED' | 'DECLINED';

export function DecisionForm(props: {
  kind: 'CREDITS' | 'SLOTS';
  decision: Decision | null;
  onDecisionChange: (decision: Decision) => void;
  note: string;
  onNoteChange: (note: string) => void;
  grantedAmount: string;
  onGrantedAmountChange: (value: string) => void;
}): ReactNode {
  const id = useId();
  const isSlots = props.kind === 'SLOTS';

  return (
    <>
      <RadioGroup
        // `''`, not `undefined`: `undefined` leaves the group owning its own
        // selection, and the first pick then switches it to controlled --
        // `RetireKeyDialog`'s grace picker is spelled the same way.
        value={props.decision ?? ''}
        onValueChange={(value) => props.onDecisionChange(value as Decision)}
        className="flex flex-row gap-4"
      >
        <div className="flex items-center gap-2">
          <RadioGroupItem value="GRANTED" id={`${id}-grant`} />
          <Label htmlFor={`${id}-grant`}>{t('decision.grant')}</Label>
        </div>
        <div className="flex items-center gap-2">
          <RadioGroupItem value="DECLINED" id={`${id}-decline`} />
          <Label htmlFor={`${id}-decline`}>{t('decision.decline')}</Label>
        </div>
      </RadioGroup>

      {props.decision === 'GRANTED' && (
        <Field>
          <FieldLabel htmlFor={`${id}-amount`}>
            {isSlots ? t('decision.slots.label') : t('decision.credits.label')}
          </FieldLabel>
          <Input
            id={`${id}-amount`}
            type="number"
            value={props.grantedAmount}
            onChange={(event) => props.onGrantedAmountChange(event.target.value)}
          />
          <FieldDescription>
            {isSlots ? t('decision.slots.hint') : t('decision.credits.hint')}
          </FieldDescription>
        </Field>
      )}

      <Field>
        <FieldLabel htmlFor={`${id}-note`}>{t('decision.note.label')}</FieldLabel>
        <Textarea
          id={`${id}-note`}
          value={props.note}
          onChange={(event) => props.onNoteChange(event.target.value)}
        />
        <FieldDescription>{t('decision.note.hint')}</FieldDescription>
      </Field>
    </>
  );
}
